import * as React from 'react';
import Tabs, { TabsProps } from '@mui/material/Tabs';
import Tab, { TabProps } from '@mui/material/Tab';
import Box, { BoxProps } from '@mui/material/Box';
import SparkScrollableGridContainer from './SparkScrollableGridContainer';
import { GridProps } from '@mui/material';

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
} & BoxProps;

function SparkTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const { sx, ...rest } = other;

  return (
    <Box
      hidden={value !== index}
      sx={{ paddingY: 3, ...sx }}
      {...rest}
      width='100%'
    >
      {value === index && children}
    </Box>
  );
}

type TabsNavigationProps = {
  tabStyles?: TabProps['sx'];
  tabHeaderStyles?: TabsProps['sx'];
  tabItems: TabItem[];
  value: number;
  handleChange: (newValue: number) => void;
  tabNavigationContainerStyles?: BoxProps['sx'];
};

const TabsNavigation = ({
  tabItems,
  value,
  tabStyles,
  tabHeaderStyles,
  handleChange,
  tabNavigationContainerStyles = {},
}: TabsNavigationProps) => {
  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: (theme) => theme.palette.borderColor,
        width: '100%',
        ...tabNavigationContainerStyles,
      }}
    >
      <Tabs
        value={value}
        onChange={(_, newItems) => handleChange(newItems)}
        sx={{
          '& .MuiTabs-indicator': {
            display: 'none',
          },

          '& .MuiTabs-flexContainer': {
            overflowX: 'auto',
            alignItems: 'center',
          },
          ...tabHeaderStyles,
        }}
      >
        {tabItems.map((tab, index) => (
          <Tab
            sx={{
              fontSize: '16px',
              color: '#000000',
              textTransform: 'capitalize',
              gap: 2,
              '&.Mui-selected': {
                backgroundColor: '#6267ac',
                border: 'none',
                color: '#ffffff',
              },
              ...tabStyles,
            }}
            label={tab.label}
            key={`${tab.label}-${index}`}
            icon={tab.icon}
            iconPosition='start'
          />
        ))}
      </Tabs>
    </Box>
  );
};

export type TabItem = {
  label: string | React.ReactNode;
  content: React.ReactNode;
  icon?: React.ReactElement | React.ReactElement;
};

type SparkTabsProps = {
  tabItems: TabItem[];
  selectedItemIndex?: number;
  tabPanelStyles?: TabProps['sx'];
  tabContainerStyles?: TabsProps['sx'];
  tabStyles?: TabProps['sx'];
  tabHeaderStyles?: TabsProps['sx'];
  onChange?: (selectedTabIndex: number) => void;
  height?: React.CSSProperties['height'];
  tabNavigationContainerStyles?: BoxProps['sx'];
  scrollableHeaderStyles?: GridProps['sx'];
  scrollableContainerStyle?: GridProps['sx'];
  scrollableGridProps?: Omit<GridProps, 'height'>;
};

function SparkTabs({
  tabItems,
  selectedItemIndex = 0,
  tabContainerStyles = {},
  scrollableHeaderStyles = {},
  tabPanelStyles = {},
  tabStyles,
  tabHeaderStyles = {},
  onChange,
  height = '100%',
  tabNavigationContainerStyles = {},
  scrollableContainerStyle = {},
  scrollableGridProps = {},
}: SparkTabsProps) {
  const [value, setValue] = React.useState(selectedItemIndex);
  React.useEffect(() => {
    setValue(selectedItemIndex);
  }, [selectedItemIndex]);

  const handleChange = (newValue: number) => {
    setValue(newValue);
    onChange && onChange(newValue);
  };
  return (
    <SparkScrollableGridContainer
      sx={{ width: '100%', ...tabContainerStyles }}
      height={height}
      headerStyle={{
        borderBottom: null,
        padding: 0,
        ...scrollableHeaderStyles,
      }}
      header={
        <TabsNavigation
          tabItems={tabItems}
          value={value}
          tabStyles={tabStyles}
          tabHeaderStyles={tabHeaderStyles}
          handleChange={handleChange}
          tabNavigationContainerStyles={tabNavigationContainerStyles}
        />
      }
      scrollableContainerStyle={{
        width: '100%',
        padding: 0,
        ...scrollableContainerStyle,
      }}
      {...scrollableGridProps}
    >
      {tabItems.map((tab, index) => (
        <SparkTabPanel
          value={value}
          index={index}
          key={index}
          sx={tabPanelStyles}
        >
          {tab.content}
        </SparkTabPanel>
      ))}
    </SparkScrollableGridContainer>
  );
}

export default SparkTabs;
